//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { GENERAL_MESSAGING_TIKTOK } from '@account-settings/components/rights-requests/constants/rightRequestConstants';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { IRequestRightsState } from '@legacy-components/rights-management/request-rights-model';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { UniversalItemType } from '@models/UniversalItemType';
import { isUndefined } from '@utils/filterPredicates';

interface HandleMentionedRequestRightsParams {
  atMentionedItems: IContentLibraryItem[];
  selectedIndex: number;
  tiktokSettings: {
    isConnected: boolean;
  };
  requestRights: IRequestRightsState;
  actions: {
    requestRightsWithOauthCheck: (item: IContentLibraryItem) => Promise<void>;
    selectMessageIndex: (index: number) => void;
    selectConfigurationId: (id: number) => void;
    setItemPendingApproval: (itemId: ItemId) => void;
    updateItemOverlayDetails: (
      partialCuid: PartialCuid,
      value: boolean,
    ) => void;
    requestRightsForAtMentioned: () => void;
  };
  callbacks: {
    onShowNoTikTokAccountReminder: () => void;
    onIncrementSelectedIndex: () => void;
  };
}

/**
 * Handle mentioned rights requests for Instagram and TikTok items
 *
 * @param params - Parameters including items, settings, and action handlers
 * @returns Promise<void>
 */
export async function processAtMentionRightsRequest(
  params: HandleMentionedRequestRightsParams,
): Promise<void> {
  const {
    actions: {
      requestRightsForAtMentioned,
      requestRightsWithOauthCheck,
      selectConfigurationId,
      selectMessageIndex,
      setItemPendingApproval,
      updateItemOverlayDetails,
    },
    atMentionedItems,
    callbacks: { onIncrementSelectedIndex, onShowNoTikTokAccountReminder },
    requestRights,
    selectedIndex,
    tiktokSettings,
  } = params;

  const currentItem = atMentionedItems[selectedIndex];
  const isTikTokItem = currentItem?.itemType === UniversalItemType.TikTok;
  const isTikTokConnected = tiktokSettings.isConnected;

  if (isTikTokItem && !isTikTokConnected) {
    onShowNoTikTokAccountReminder();
    return;
  }

  if (isUndefined(currentItem)) {
    return;
  }

  if (isTikTokItem) {
    const { configurations } = requestRights;

    const tikTokConfigEntry = Object.entries(configurations.byId).find(
      ([, config]) =>
        config?.name?.toLowerCase() === GENERAL_MESSAGING_TIKTOK.toLowerCase(),
    );

    if (tikTokConfigEntry) {
      const [configId, tikTokConfig] = tikTokConfigEntry;
      const numMessages = tikTokConfig?.rightsRequestData?.data?.length || 1;
      const messageIndex = selectedIndex % numMessages;
      selectConfigurationId(parseInt(configId, 10));
      selectMessageIndex(messageIndex);
    }

    await requestRightsWithOauthCheck(currentItem);
    setItemPendingApproval(currentItem.id);

    const partialCuid = PartialCuid.build(currentItem);
    updateItemOverlayDetails(partialCuid, true);

    // Move to next item if available
    if (selectedIndex < atMentionedItems.length - 1) {
      onIncrementSelectedIndex();
    }
  } else {
    // Handle Instagram rights request flow
    requestRightsForAtMentioned();
  }
}

interface HandleTikTokDoneParams {
  nonAtMentionedItems: IContentLibraryItem[];
  actions: {
    closeModal: () => void;
    openNonMentionedModal: (items: IContentLibraryItem[]) => void;
    clearSelectedItems: () => void;
  };
}

/**
 * Handle TikTok completion flow
 * Opens non-mentioned modal if items exist, otherwise clears and closes
 *
 * @param params - Parameters including non-mentioned items and action handlers
 */
export function completeRightsWorkflow(params: HandleTikTokDoneParams): void {
  const {
    actions: { clearSelectedItems, closeModal, openNonMentionedModal },
    nonAtMentionedItems,
  } = params;

  if (nonAtMentionedItems.length > 0) {
    // If there are non-mentioned items, proceed to that modal
    openNonMentionedModal(nonAtMentionedItems);
  } else {
    // Otherwise, clear selections
    clearSelectedItems();
  }

  closeModal();
}
